import { useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Image from "@/ui/Image";
import { consultant_helper } from "@/helpers/consultant_helper";
import classes from "./styles.module.scss";
import Location from "@/assets/Location.svg";
import Phone from "@/assets/Phone.svg";
import Alias from "@/assets/Alias.svg";
import LinkedIn from "@/assets/LinkedIn.svg";

const W = 24,
  H = 24;
const norm = (v) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

function useSelectedConsultants(consultantsProp) {
  return useMemo(() => {
    const pool = consultant_helper.fetch?.({}) || consultant_helper.fetch?.() || [];
    const normalizeRow = (c) => ({
      id: c.id ?? c._id ?? null,
      url_slug: c.url_slug ?? "",
      consultant: c.consultant ?? "",
      position: c.position ?? "",
      email: c.email ?? "",
      phone_number: c.phone_number ?? "",
      location: c.location ?? "",
      linkedin_url: c.linkedin_url ?? "",
      profile_picture: c.profile_picture ?? "",
      description: c.description ?? "",
      back_to_team_link: c.back_to_team_link ?? "",
    });
    const all = pool.map(normalizeRow);

    const deDupKey = (c) => norm(c.id || c.url_slug || c.consultant);
    const dedupe = (arr) => {
      const seen = new Set();
      return arr.filter((c) => {
        const k = deDupKey(c);
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };

    const noSelection =
      !consultantsProp ||
      (Array.isArray(consultantsProp) && consultantsProp.length === 0) ||
      (typeof consultantsProp === "string" && consultantsProp.trim() === "");

    if (noSelection) return dedupe(all).slice(0, 5);

    const wanted = Array.isArray(consultantsProp) ? consultantsProp : [consultantsProp];
    const out = [];
    const used = new Set();

    wanted.forEach((w) => {
      const t = norm(w);
      if (!t) return;
      const match = all.find((c) => {
        const k = deDupKey(c);
        if (!k || used.has(k)) return false;
        return [c.id, c.url_slug, c.consultant].map(norm).includes(t);
      });
      if (match) {
        used.add(deDupKey(match));
        out.push(match);
      }
    });

    return out.length ? out : [];
  }, [consultantsProp]);
}

const splitEmails = (val) =>
  String(val ?? "")
    .split(/[,\n;]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

const splitPhones = (val) =>
  String(val ?? "")
    .split(/[,\n;]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

const normalizeEmailList = (input) => {
  const out = [];
  if (Array.isArray(input)) {
    input.forEach((item) => {
      if (item && typeof item === "object" && "emailAddress" in item) {
        out.push(...splitEmails(item.emailAddress));
      } else if (typeof item === "string") {
        out.push(...splitEmails(item));
      }
    });
  } else if (typeof input === "string") {
    out.push(...splitEmails(input));
  }
  return out;
};

const normalizePhoneList = (input) => {
  const out = [];
  if (Array.isArray(input)) {
    input.forEach((item) => {
      if (item && typeof item === "object" && "phoneNumber" in item) {
        out.push(...splitPhones(item.phoneNumber));
      } else if (typeof item === "string") {
        out.push(...splitPhones(item));
      }
    });
  } else if (typeof input === "string") {
    out.push(...splitPhones(input));
  }
  return out;
};

export const ConsultantsList = ({
  title,
  content,
  consultants,
  newLocation,
  newEmail,
  newPhoneNumber,
  showLocation = true,
  showEmail = true,
  showPhoneNumber = true,
}) => {
  const selected = useSelectedConsultants(consultants);
  const emailList = useMemo(() => normalizeEmailList(newEmail), [newEmail]);
  const phoneList = useMemo(() => normalizePhoneList(newPhoneNumber), [newPhoneNumber]);

  return (
    <div>
      <Container>
        <div className="my-5 gx-5">
          {title && <h2 className={classes.content}>{title}</h2>}
          {content && <div className={`${classes.content} mb-4`} dangerouslySetInnerHTML={{ __html: content }} />}

          <Row>
            {selected.map((c, idx) => {
              const loc = typeof newLocation === "string" && newLocation.trim() ? newLocation.trim() : c.location;
              const emailOverride = emailList[idx] && emailList[idx].length ? emailList[idx] : c.email;
              const phoneOverride = phoneList[idx] && phoneList[idx].length ? phoneList[idx] : c.phone_number;
              return (
                <>
                  <Col xs={12} lg={4}>
                    <div key={c.id || c.url_slug || idx} className={classes.recruiter}>
                      <div className={classes.recruiter__profilepicture}>
                        <Image
                          img={c.profile_picture || "https://placehold.co/200"}
                          alt={c.consultant || "Profile Picture placeholder"}
                          size="200x200"
                        />
                      </div>
                      <div>
                        <h3 className={classes.recruiter__name}>{c.consultant || "-"}</h3>
                        <span className={classes.recruiter__position}>{c.position || "-"}</span>
                        <div className="mt-2">
                          {showLocation && (
                            <span className="d-flex align-items-center gap-2">
                              <Location style={{ width: W, height: H }} />
                              <b>{loc || "-"}</b>
                            </span>
                          )}
                          {showEmail && (
                            <span className="d-flex align-items-center gap-2">
                              <Alias style={{ width: W, height: H }} />
                              {emailOverride ? <a href={`mailto:${emailOverride}`}>{emailOverride}</a> : <span>-</span>}
                            </span>
                          )}
                          {showPhoneNumber && (
                            <span className="d-flex align-items-center gap-2">
                              <Phone style={{ width: W, height: H }} />
                              {phoneOverride ? <a href={`tel:${phoneOverride}`}>{phoneOverride}</a> : <span>-</span>}
                            </span>
                          )}
                          {c.linkedin_url && c.linkedin_url !== "#" ? (
                            <span className="d-flex align-items-center gap-2">
                              <LinkedIn style={{ width: W, height: H }} />
                              <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer">
                                LinkedIn Profile
                              </a>
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Col>
                </>
              );
            })}
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default ConsultantsList;
